import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit.svg';
import { ReactComponent as LocationSelector } from '../../../../../assets/icons/location-selector.svg';
import { ReactComponent as NextArrowIcon } from '../../../../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrowIcon } from '../../../../../assets/icons/prev-arrow.svg';
import DefaultStationImage from '../../../../../assets/images/default-station.png';
import NoDataFound from '../../../../../assets/images/no-data-found.png';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import {
  fetchStationDetailsData,
  removeStationDetails,
} from '../../../../../redux/slices/StationDetailsSlice';
import MyButton from '../../../../../shared-components/button/Button';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SelectLatLong from '../../../../../shared-components/select-location/components/SelectLatLong';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import StationSidePanel from '../../../../../shared-components/table-side-panel/StationSidePanel';
import TabsContainer from '../../../../../shared-components/tabs-container/TabsContainer';
import { convertTo12HourFormat } from '../../../../../shared-functions/DataTime';
import { permissionCheck } from '../../../../../shared-functions/JWTDocodeFunction';
import chargingStation from '../../../../../utils/api/partner/station';
import SelectAmenities from '../../../station-management/components/SelectAmenities';
import DashboardWidgets from '../DashboardWidgets';

import StationImagesModal from './StationImagesModal';

/**
 * Helper to format open/close times to 12h format
 * @param {any} dayData - The dayData
 * @param {any} closedLabel - The closedLabel
 * @param {any} open24Label - The open24Label
 */
const getFormattedHours = (dayData: any, closedLabel: string, open24Label: string) => {
  if (!dayData) return 'N/A';
  const isOpen = dayData.isOpen || dayData.status === 'open';
  if (!isOpen) return closedLabel;
  const openTime = dayData.from || dayData.open;
  const closeTime = dayData.to || dayData.close;
  if (openTime === '00:00' && closeTime === '23:59') {
    return open24Label;
  }
  const formattedOpen = openTime ? convertTo12HourFormat(openTime).toLowerCase() : 'N/A';
  const formattedClose = closeTime ? convertTo12HourFormat(closeTime).toLowerCase() : 'N/A';
  return `${formattedOpen} - ${formattedClose}`;
};

/**
 * Station details component
 * @returns {any} The rendered component
 */
export function StationDetailsLeft() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { stationDetails, isLoading } = useAppSelector((state) => state.stationDetails);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const [openTab, setOpenTab] = useState(0);
  const [editStationOpen, setEditStationOpen] = useState(false);
  const [locationPopup, setLocationPopup] = useState<string>('close');

  const containerTabs = [
    {
      label: t('stationDetail', 'Station Detail'),
      key: 'stationDetail',
      isActive: openTab === 0,
    },
    {
      label: t('charger', 'Charger'),
      key: 'charger',
      isActive: openTab === 1,
    },
    {
      label: t('navigationDirections', 'Navigation Directions'),
      key: 'navigationDirections',
      isActive: openTab === 2,
    },
  ];

  /**
   * Handles switching tabs
   * @param {string} key - Selected tab key
   */
  const handleSwitchTab = (key: string) => {
    if (key === 'stationDetail') {
      setOpenTab(0);
    } else if (key === 'charger') {
      setOpenTab(1);
    } else if (key === 'navigationDirections') {
      setOpenTab(2);
    }
  };

  /**
   * Fetch the account details in redux
   */
  const fetchData = () => {
    if (selectedChargingPoint?.id) {
      dispatch(fetchStationDetailsData(selectedChargingPoint.id));
    }
  };

  useEffect(() => {
    if (stationDetails && stationDetails?.submission?.status !== 'approved') {
      dispatch(removeStationDetails());
      navigate('/ev-admin/operations/charging-station/approved');
    }
    if (selectedChargingPoint?.id) {
      fetchData();
    }
  }, [selectedChargingPoint]);

  return (
    <div className="station-details-left-station-details" dir={i18n.dir()}>
      <TabsContainer containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />

      <div className="tab-content-container">
        {(() => {
          if (isLoading) {
            return (
              <div className="skeleton-grid-loader">
                <div className="skeleton-card" />
              </div>
            );
          }

          if (!stationDetails) {
            return (
              <div className="table-no-data-wrap">
                <img src={NoDataFound} alt="" className="no-data-found-image" />
                <div className="no-data-found-details">
                  <h3>{t('noStationDetailsTitle')}</h3>
                  <p>{t('noStationDetailsDesc')}</p>
                </div>
              </div>
            );
          }

          if (openTab === 0) {
            return (
              <>
                {' '}
                <div className="station-actions-wrapper">
                  <MyButton
                    onClickFunc={() => setEditStationOpen(true)}
                    label={t('editStation')}
                    buttonType="outline"
                    svgIcon={<EditIcon />}
                    disableClass="add-device-btn-custom"
                    disableButton={
                      !permissionCheck('partnerChargingStationUpdate') &&
                      !permissionCheck('cpoChargingStationUpdate')
                    }
                  />
                </div>
                <div className="left">
                  {/* Left 50%: Station Details */}
                  <div className="left-column">
                    <div className="info">
                      <p className="info-value">{stationDetails.name || '-'}</p>
                      <p className="info-label">{t('stationName')}</p>
                    </div>

                    <div className="info">
                      <p className="info-value">
                        {stationDetails.isAvailable ? t('available') : t('unavailable')}
                      </p>
                      <p className="info-label">{t('publiclyAccess')}</p>
                    </div>
                    <div className="info">
                      <p className="info-value">
                        {stationDetails.latitude || '-'}, {stationDetails.longitude || '-'}
                      </p>
                      <p className="info-label">{t('Map Location')}</p>
                    </div>

                    <div className="info">
                      <p className="info-value">
                        {(stationDetails as any).countryCode ||
                        (stationDetails as any).emergencyContact
                          ? `+${(stationDetails as any).countryCode || ''} ${(stationDetails as any).emergencyContact || ''}`
                          : '-'}
                      </p>
                      <p className="info-label">{t('emergencyContact', 'Emergency Contact')}</p>
                    </div>

                    <div className="info address">
                      <p className="info-value">
                        <span
                          role="presentation"
                          onClick={() => setLocationPopup('open')}
                          className="location-trigger">
                          <LocationSelector />
                        </span>
                        {stationDetails.address || '-'}
                      </p>
                      <p className="info-label">{t('address')}</p>
                    </div>
                  </div>

                  {/* Right 50%: Operating Hours */}
                  <div className="left-column">
                    <div className="info">
                      <p className="info-value">{(stationDetails as any).time_zone || '-'}</p>
                      <p className="info-label">{t('timeZone', 'Time Zone')}</p>
                    </div>

                    <div className="info operating-hours-container">
                      <p className="info-label">{t('operatingHrs')}</p>
                      <div className="operating-hours-grid">
                        {(() => {
                          const hours = stationDetails.operatingHours;
                          if (!hours) return <p className="info-value">N/A</p>;

                          const days = [
                            'monday',
                            'tuesday',
                            'wednesday',
                            'thursday',
                            'friday',
                            'saturday',
                            'sunday',
                          ] as const;

                          const dayNames = {
                            monday: t('monday', 'Monday'),
                            tuesday: t('tuesday', 'Tuesday'),
                            wednesday: t('wednesday', 'Wednesday'),
                            thursday: t('thursday', 'Thursday'),
                            friday: t('friday', 'Friday'),
                            saturday: t('saturday', 'Saturday'),
                            sunday: t('sunday', 'Sunday'),
                          };

                          return days.map((day) => {
                            const dayData = hours[day];
                            const isOpen = dayData?.isOpen || dayData?.status === 'open';
                            const formattedHours = getFormattedHours(
                              dayData,
                              t('closed'),
                              t('open24Hours', 'Open 24 hours'),
                            );

                            return (
                              <div key={day} className="operating-hour-row">
                                <span className="day-label">{dayNames[day]}:</span>
                                <span className={`time-label${!isOpen ? ' closed' : ''}`}>
                                  {formattedHours}
                                </span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          }

          if (openTab === 1) {
            return (
              <div className="charger-tab-container">
                <DashboardWidgets />
              </div>
            );
          }
          if (openTab === 2) {
            const directionsList = (stationDetails as any)?.navigationDirections || [];

            return (
              <div className="navigation-directions-tab">
                <div className="directions-header">
                  <div className="header-title-container">
                    <LocationSelector className="directions-icon" />
                    <span className="directions-title">{t('directions', 'Directions')}</span>
                    <span className="directions-count">{directionsList.length}</span>
                  </div>
                  <MyButton
                    onClickFunc={() => setEditStationOpen(true)}
                    label={t('edit', 'Edit')}
                    buttonType="outline"
                    svgIcon={<EditIcon />}
                    disableClass="add-device-btn-custom"
                    disableButton={
                      !permissionCheck('partnerChargingStationUpdate') &&
                      !permissionCheck('cpoChargingStationUpdate')
                    }
                  />
                </div>
                <div className="directions-list">
                  {directionsList.length === 0 ? (
                    <div className="no-directions">
                      <p>{t('noDirectionsFound', 'No navigation directions found.')}</p>
                    </div>
                  ) : (
                    directionsList.map((item: any, index: number) => (
                      <div key={index} className="direction-card">
                        <div className="direction-top-bar">
                          {item.language && (
                            <span className="direction-lang-badge">
                              {item.language === 'Arabic'
                                ? 'AR'
                                : item.language.substring(0, 2).toUpperCase()}
                            </span>
                          )}
                          {item.country && (
                            <span className="direction-country-label">{item.country}</span>
                          )}
                        </div>
                        <div className="direction-content">{item.direction}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          }

          return null;
        })()}
      </div>
      {/* Edit Station Panel */}
      <SidePaneModel
        open={editStationOpen}
        closePanel={() => setEditStationOpen(false)}
        content={
          <StationSidePanel
            fetchIdFrom="props"
            id={selectedChargingPoint?.id as string}
            initialIsEditMode
            onEditModeChange={(isEditMode) => {
              if (!isEditMode) setEditStationOpen(false);
            }}
            onClose={() => setEditStationOpen(false)}
            onSuccess={fetchData}
          />
        }
        position="right"
        hideHeader
      />

      <ModalBox
        open={locationPopup === 'open'}
        title={t('selectLocationTitle', 'Select Location')}
        content={
          <SelectLatLong
            latitude={stationDetails?.latitude || null}
            longitude={stationDetails?.longitude || null}
            handleLatLongChange={() => setLocationPopup('close')}
          />
        }
        closeModal={() => setLocationPopup('close')}
        header
        modalBoxAnimation="modalAnimateToLeft"
      />
    </div>
  );
}

interface StationDetailsRightProperties {
  showTabs?: boolean;
  defaultTab?: 'media' | 'amenities';
}

/**
 * Formats a size in bytes to a human-readable string representation
 * @param {any} bytes - The number of bytes to format
 * @param {any} decimalPlaces - The number of decimal places to keep
 * @returns {any} A formatted string with the appropriate size unit (e.g. KB, MB)
 */
const formatBytes = (bytes: number, decimalPlaces = 2) => {
  if (!bytes) return '0 Bytes';
  const kilo = 1024;
  const dmValue = decimalPlaces < 0 ? 0 : decimalPlaces;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(kilo));
  return `${parseFloat((bytes / kilo ** sizeIndex).toFixed(dmValue))} ${sizes[sizeIndex]}`;
};



/**
 * Helper to append mandatory station details to FormData for editStation API call
 * @param {FormData} formData - The FormData object
 * @param {any} stationDetailsData - The current station details object
 */
const appendStationDataToFormData = (formData: FormData, stationDetailsData: any) => {
  if (!stationDetailsData) return;
  if (stationDetailsData.name) {
    formData.append('name', stationDetailsData.name);
  }
  if (stationDetailsData.latitude !== undefined && stationDetailsData.latitude !== null) {
    formData.append('latitude', String(stationDetailsData.latitude));
  }
  if (stationDetailsData.longitude !== undefined && stationDetailsData.longitude !== null) {
    formData.append('longitude', String(stationDetailsData.longitude));
  }
  if (stationDetailsData.address) {
    formData.append('address', stationDetailsData.address);
  }
  const timeZone = stationDetailsData.time_zone || stationDetailsData.timeZone;
  if (timeZone) {
    formData.append('time_zone', timeZone);
  }

  if (stationDetailsData.operatingHours) {
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ] as const;

    days.forEach((day) => {
      const dayData = stationDetailsData.operatingHours?.[day];
      if (dayData) {
        const isOpen = dayData.isOpen || dayData.status === 'open';
        const openTime = dayData.from || dayData.open || '00:00';
        const closeTime = dayData.to || dayData.close || '23:59';

        if (isOpen) {
          formData.append(`operatingHours[${day}][open]`, openTime);
          formData.append(`operatingHours[${day}][close]`, closeTime);
          formData.append(`operatingHours[${day}][status]`, 'open');
        } else {
          formData.append(`operatingHours[${day}][status]`, 'closed');
        }
      }
    });
    if (stationDetailsData.operatingHours.allDay !== undefined) {
      formData.append('operatingHours[allDay]', String(stationDetailsData.operatingHours.allDay));
    }
  }
};

/**
 * Station details right panel
 * @param {StationDetailsRightProperties} properties - Component props
 * @returns {any} The rendered component
 */
export function StationDetailsRight(properties: StationDetailsRightProperties) {
  const { showTabs = true, defaultTab = 'media' } = properties;
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'media' | 'amenities'>(defaultTab);
  const { stationDetails, isLoading } = useAppSelector((state) => state.stationDetails);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // Image upload modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const previewImages = React.useMemo(() => {
    if (stationDetails?.medias && stationDetails.medias.length > 0) {
      return stationDetails.medias.map((media) => media.signedUrl || media.url);
    }
    return [DefaultStationImage];
  }, [stationDetails?.medias]);

  /**
   * Navigate to previous image in preview carousel
   */
  const handlePreviousImage = () => {
    setSelectedImageIndex((previousIndex) =>
      previousIndex !== null
        ? (previousIndex - 1 + previewImages.length) % previewImages.length
        : 0,
    );
  };

  /**
   * Navigate to next image in preview carousel
   */
  const handleNextImage = () => {
    setSelectedImageIndex((previousIndex) =>
      previousIndex !== null ? (previousIndex + 1) % previewImages.length : 0,
    );
  };

  useEffect(() => {
    if (selectedImageIndex === null) return undefined;
    /**
     *handlekeydown function
     */
    const handleKeyDown = (event_: KeyboardEvent) => {
      if (event_.key === 'ArrowLeft') {
        handlePreviousImage();
      } else if (event_.key === 'ArrowRight') {
        handleNextImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImageIndex, previewImages.length]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const currentTab = showTabs ? activeTab : defaultTab;



  /**
   * Close the edit modal and discard unsaved selections
   */
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  /**
   * Delete a station media image by URL
   * @param {string} mediaUrl - The URL of the media image to delete
   * @param {React.MouseEvent} event_ - The mouse click event
   */
  const handleDeleteMedia = async (mediaUrl: string, event_: React.MouseEvent) => {
    event_.stopPropagation();
    if (!stationDetails?._id || isSaving) return;

    setIsSaving(true);
    const formData = new FormData();
    formData.append('removedUrls[0]', mediaUrl);
    appendStationDataToFormData(formData, stationDetails);

    try {
      await chargingStation.editStation(stationDetails._id, formData, () => {});

      dispatch(
        setNotificationMessage({
          status: true,
          message: t('imageDeletedSuccessfully', 'Station image deleted successfully.'),
          type: 'success',
        }),
      );

      dispatch(fetchStationDetailsData(stationDetails._id));
    } catch (error: any) {
      console.error('Failed to delete station image:', error);
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message ||
            error?.message ||
            t('failedToDeleteImage', 'Failed to delete station image.'),
          type: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="station-details-right-images-amenities" dir={i18n.dir()}>
      {/* Buttons */}
      {showTabs && (
        <div className="station-details-top">
          <MyButton
            label={t('media')}
            buttonType={activeTab === 'media' ? 'submit' : 'primary-outline'}
            onClickFunc={() => setActiveTab('media')}
          />
          <MyButton
            label={t('amenities')}
            buttonType={activeTab === 'amenities' ? 'submit' : 'primary-outline'}
            onClickFunc={() => setActiveTab('amenities')}
          />
        </div>
      )}

      {isLoading ? (
        <div className="skeleton-grid-loader">
          {[...Array(5)].map((index) => (
            <div className="skeleton-card" key={index} />
          ))}
        </div>
      ) : (
        <div
          className={`station-details-bottom ${currentTab === 'amenities' ? 'amenities-tab' : ''}`}>
          {currentTab === 'media' && (
            <div className="station-details-bottom">
              {stationDetails?.medias && stationDetails.medias.length > 0 ? (
                stationDetails.medias.map((media, index) => (
                  <div
                    role="presentation"
                    key={media._id || index}
                    className="image-wrapper"
                    onClick={() => setSelectedImageIndex(index)}>
                    <img
                      src={media.signedUrl || media.url}
                      alt={`${t('station')} ${index}`}
                      onError={(event_) => {
                        const img = event_.currentTarget as HTMLImageElement;
                        img.src = DefaultStationImage;
                      }}
                    />
                    {(permissionCheck('partnerChargingStationDelete') ||
                      permissionCheck('cpoChargingStationDelete')) && (
                      <button
                        type="button"
                        className="delete-media-icon-btn"
                        title={t('delete', 'Delete')}
                        onClick={(event_) => handleDeleteMedia(media.url, event_)}>
                        <DeleteIcon />
                      </button>
                    )}
                    {media.fileSize ? (
                      <div className="image-size-badge">{formatBytes(Number(media.fileSize))}</div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div
                  role="presentation"
                  className="image-wrapper"
                  onClick={() => setSelectedImageIndex(0)}>
                  <img src={DefaultStationImage} alt={t('station')} />
                </div>
              )}

              {/* Edit/Add images trigger card */}
              {(
                permissionCheck('partnerChargingStationUpdate') ||
                permissionCheck('cpoChargingStationUpdate')) && (
                <div
                  role="presentation"
                  className="image-wrapper add-trigger-card"
                  onClick={() => setIsEditModalOpen(true)}>
                  <div className="add-trigger-content">
                    <span className="plus-icon">+</span>
                    <span className="add-text">{t('addEdit', 'Add / Edit / Delete')}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentTab === 'amenities' && (
            <div className="amenity-wrapper">
              <SelectAmenities
                selectedAmenities={stationDetails?.amenities || []}
                handleCancel={() => {}}
                handleStationDetailChange={() => {}}
                hideButtons
                hideRemove
              />
            </div>
          )}
        </div>
      )}

      <ModalBox
        open={selectedImageIndex !== null}
        title={t('imagePreview', 'Image Preview')}
        closeModal={() => setSelectedImageIndex(null)}
        header
        position="center"
        content={
          <div className="image-preview-modal-content">
            {previewImages.length > 1 && (
              <button
                type="button"
                className="carousel-nav-btn prev-btn"
                aria-label={t('previousImage', 'Previous Image')}
                onClick={handlePreviousImage}>
                <PreviousArrowIcon />
              </button>
            )}
            <div className="preview-image-container">
              <img
                src={
                  selectedImageIndex !== null && previewImages[selectedImageIndex]
                    ? previewImages[selectedImageIndex]
                    : DefaultStationImage
                }
                alt="Preview"
                onError={(event_) => {
                  const img = event_.currentTarget as HTMLImageElement;
                  img.src = DefaultStationImage;
                }}
              />
              {previewImages.length > 1 && selectedImageIndex !== null && (
                <div className="carousel-counter">
                  {selectedImageIndex + 1} / {previewImages.length}
                </div>
              )}
            </div>
            {previewImages.length > 1 && (
              <button
                type="button"
                className="carousel-nav-btn next-btn"
                aria-label={t('nextImage', 'Next Image')}
                onClick={handleNextImage}>
                <NextArrowIcon />
              </button>
            )}
          </div>
        }
      />

      <StationImagesModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        stationDetails={stationDetails}
        onSuccess={() => {
          if (stationDetails?._id) {
            dispatch(fetchStationDetailsData(stationDetails._id));
          }
        }}
      />
    </div>
  );
}

StationDetailsRight.defaultProps = {
  showTabs: true,
  defaultTab: 'media',
};

/**
 * StationDetails component
 * @returns {any} The rendered component
 */
function StationDetails() {
  const { i18n } = useTranslation('sidePanel');
  return (
    <div className="station-details-wrap" dir={i18n.dir()}>
      <StationDetailsLeft />
      <div className="station-details-horizontal-line" />
      <StationDetailsRight />
    </div>
  );
}

export default StationDetails;
